/**
 * Authentication Context
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  getUserData,
  isAuthenticated as checkIsAuthenticated,
  getToken,
  isTokenExpired,
  clearAuthData,
  UserData,
  setUserData,
} from '../utils/tokenManager';
import { logout as logoutService, refreshToken } from '../services/authService';
import IdleWarningDialog from '../components/IdleWarningDialog';

interface AuthContextType {
  user: UserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: UserData) => void;
  logout: () => Promise<void>;
  updateUser: (userData: UserData) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// How often to check if token needs refresh (1 minute)
const TOKEN_CHECK_INTERVAL = 60 * 1000;
// Consider user active if they've interacted within last 5 minutes
const ACTIVITY_THRESHOLD = 5 * 60 * 1000;
// Sign out after 15 minutes with no activity in any tab (FLHSMV External Information Security Policy #B-02, OP#425)
const IDLE_TIMEOUT = 15 * 60 * 1000;
// Warn this long before the idle sign-out, with a countdown
const IDLE_WARNING = 60 * 1000;
// Checked every second so the countdown ticks; it only reads a ref and one localStorage key
const IDLE_CHECK_INTERVAL = 1000;
// Last activity is shared through localStorage so a tab left idle does not sign out a tab in use;
// written at most this often to keep mousemove cheap
const SHARED_ACTIVITY_KEY = 'aas_last_activity';
const SHARED_ACTIVITY_WRITE_INTERVAL = 15 * 1000;

const readSharedActivity = (): number => {
  try {
    return Number(localStorage.getItem(SHARED_ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeSharedActivity = (at: number): void => {
  try {
    localStorage.setItem(SHARED_ACTIVITY_KEY, String(at));
  } catch {
    // Storage unavailable: idle tracking falls back to this tab's own activity
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastActivityRef = useRef<number>(Date.now());
  const refreshInProgressRef = useRef<boolean>(false);
  const lastSharedWriteRef = useRef<number>(0);
  // Seconds until idle sign-out while the warning is showing, otherwise null
  const [idleSecondsLeft, setIdleSecondsLeft] = useState<number | null>(null);
  // Mirrors idleSecondsLeft for the event handlers, which must not wait for a render
  const idleWarningShownRef = useRef<boolean>(false);
  const idleLogoutStartedRef = useRef<boolean>(false);

  const markActive = useCallback((now: number) => {
    lastActivityRef.current = now;
    if (now - lastSharedWriteRef.current >= SHARED_ACTIVITY_WRITE_INTERVAL) {
      lastSharedWriteRef.current = now;
      writeSharedActivity(now);
    }
  }, []);

  // Track user activity
  const updateActivity = useCallback((event?: Event) => {
    // Chrome sends a mousemove with no movement whenever the element under a resting pointer
    // changes, including when the idle warning itself appears; that is not the user
    if (event instanceof MouseEvent && event.type === 'mousemove'
      && event.movementX === 0 && event.movementY === 0) {
      return;
    }
    // While the warning is up only "Stay signed in" keeps the session (stayActive below)
    if (idleWarningShownRef.current) return;

    // Past the timeout nothing revives the session: returning to a tab Chrome had paused
    // must sign out, not count the first mouse movement as activity
    const now = Date.now();
    if (now - Math.max(lastActivityRef.current, readSharedActivity()) >= IDLE_TIMEOUT) return;

    markActive(now);
  }, [markActive]);

  // Check if user has been active recently
  const isUserActive = useCallback(() => {
    return Date.now() - lastActivityRef.current < ACTIVITY_THRESHOLD;
  }, []);

  // Background token refresh for active users
  useEffect(() => {
    if (!user) return;

    // Set up activity listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Periodic token check
    const checkAndRefreshToken = async () => {
      const token = getToken();
      if (!token) return;

      // Only refresh if user has been active and token is expiring soon
      if (isUserActive() && isTokenExpired(token)) {
        if (refreshInProgressRef.current) return;

        refreshInProgressRef.current = true;
        try {
          const result = await refreshToken();
          if (result) {
            // Update user data if it changed
            setUser(result.user);
            setUserData(result.user);
          } else {
            // Refresh failed - clear auth and redirect
            clearAuthData();
            setUser(null);
            window.location.href = '/login';
          }
        } catch (error) {
          console.error('Background token refresh failed:', error);
        } finally {
          refreshInProgressRef.current = false;
        }
      }
    };

    const intervalId = setInterval(checkAndRefreshToken, TOKEN_CHECK_INTERVAL);

    // Idle sign-out: the screen may be showing owner records, so it must not stay up unattended
    const checkIdle = async () => {
      if (idleLogoutStartedRef.current) return;

      const lastActivity = Math.max(lastActivityRef.current, readSharedActivity());
      const remaining = IDLE_TIMEOUT - (Date.now() - lastActivity);
      if (remaining > IDLE_WARNING) {
        idleWarningShownRef.current = false;
        setIdleSecondsLeft(null);
        return;
      }
      if (remaining > 0) {
        idleWarningShownRef.current = true;
        setIdleSecondsLeft(Math.ceil(remaining / 1000));
        return;
      }

      idleLogoutStartedRef.current = true;
      idleWarningShownRef.current = false;
      setIdleSecondsLeft(null);
      try {
        await logoutService();
      } catch (error) {
        console.error('Idle logout error:', error);
      }
      clearAuthData();
      setUser(null);
      window.location.href = '/login?reason=idle';
    };

    const idleIntervalId = setInterval(checkIdle, IDLE_CHECK_INTERVAL);

    // Chrome slows or pauses timers in hidden tabs; check the moment the tab is shown again
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
      clearInterval(idleIntervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, updateActivity, isUserActive]);

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        if (checkIsAuthenticated()) {
          const token = getToken();

          // Validate token expiration
          if (token && isTokenExpired(token)) {
            console.warn('Token expired during initialization, clearing auth data');
            clearAuthData();
            setUser(null);
          } else {
            // Token is valid, load user data
            const userData = getUserData();
            setUser(userData);
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        clearAuthData();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (userData: UserData) => {
    idleLogoutStartedRef.current = false;
    idleWarningShownRef.current = false;
    lastActivityRef.current = Date.now(); // Reset activity on login
    writeSharedActivity(lastActivityRef.current);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await logoutService();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      clearAuthData();
    }
  };

  // "Stay signed in" on the idle warning: the one thing that keeps the session while it shows,
  // counted as activity in every tab straight away
  const stayActive = useCallback(() => {
    idleWarningShownRef.current = false;
    lastSharedWriteRef.current = 0;
    markActive(Date.now());
    setIdleSecondsLeft(null);
  }, [markActive]);

  const updateUser = (userData: UserData) => {
    setUser(userData);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {user && idleSecondsLeft !== null && (
        <IdleWarningDialog secondsLeft={idleSecondsLeft} onStay={stayActive} />
      )}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
